<script lang="ts">
  /**
   * Kehitys: streaks, weight line chart, weekly bars and last-30 tiles.
   * Everything is precomputed by GET /api/progress; this file only draws.
   * No calories anywhere.
   */
  import { apiFetch } from '../lib/api';
  import { errorMessage } from '../lib/errors';
  import { addDays, diffDays, fmtDay, fmtDelta, fmtKg, fmtShort, fromKey } from '../lib/format';
  import type { Progress } from '../lib/types';
  import LoadState from '../components/LoadState.svelte';
  import Pill from '../components/Pill.svelte';
  import StatTile from '../components/StatTile.svelte';

  // Route params are accepted for the router's component type; this view has none.
  let _props: { params?: Record<string, string> } = $props();

  let data = $state<Progress | null>(null);
  let loading = $state(false);
  let error = $state<string | null>(null);

  async function load(): Promise<void> {
    loading = true;
    error = null;
    try {
      data = await apiFetch<Progress>('/api/progress');
    } catch (err) {
      error = errorMessage(err);
    } finally {
      loading = false;
    }
  }

  $effect(() => { void load(); });

  /* ---- weight chart (42-day window ending today) ---- */
  const W = 400, H = 170, L = 34, R = 12, T = 14, B = 26;
  const SPAN = 41;
  const xs = (i: number): number => L + (i / SPAN) * (W - L - R);

  interface WPoint { i: number; kg: number; date: string }

  let weightChart = $derived.by(() => {
    if (!data) return null;
    const today = fromKey(data.today);
    const pts: WPoint[] = [];
    for (const w of data.weights) {
      const i = SPAN - diffDays(w.date, today);
      if (i >= 0 && i <= SPAN) pts.push({ i, kg: w.kg, date: w.date });
    }
    if (pts.length < 2) return { pts, enough: false as const };
    const kgs = pts.map((p) => p.kg);
    const lo = Math.floor(Math.min(...kgs) - 0.5);
    const hi = Math.ceil(Math.max(...kgs) + 0.5);
    const ys = (kg: number): number => T + ((hi - kg) / (hi - lo)) * (H - T - B);
    const gridLines: number[] = [];
    // Keep the gridline count sane for wide ranges.
    const step = hi - lo > 12 ? Math.ceil((hi - lo) / 8) : 1;
    for (let v = lo; v <= hi; v += step) gridLines.push(v);
    const path = pts.map((p, k) => `${k ? 'L' : 'M'}${xs(p.i).toFixed(1)},${ys(p.kg).toFixed(1)}`).join('');
    const first = pts[0];
    const last = pts[pts.length - 1];
    const area = `${path}L${xs(last.i).toFixed(1)},${ys(lo).toFixed(1)}L${xs(first.i).toFixed(1)},${ys(lo).toFixed(1)}Z`;
    const xLabels: { x: number; text: string }[] = [];
    for (let wk = 0; wk < 6; wk++) {
      const i = wk * 7;
      xLabels.push({ x: xs(i), text: fmtShort(addDays(today, -(SPAN - i))) });
    }
    return {
      enough: true as const,
      pts, lo, hi, ys, gridLines, path, area, xLabels,
      last,
      delta: last.kg - first.kg,
    };
  });

  /* ---- weekly bars ---- */
  const WH = 190, bT = 22, bB = 30, bw = 18, gap = 3;
  const gw = (W - L - R) / 6;
  const yb = (v: number): number => bT + ((7 - v) / 7) * (WH - bT - bB);
</script>

<div class="topbar"><div class="grow"><div class="sub">Viimeiset 6 viikkoa</div><h1>Kehitys</h1></div></div>

{#if !data}
  <LoadState {loading} {error} onretry={load} />
{:else}
  <div class="streak">
    <StatTile tone="good" big value={String(data.streaks.fullDays)} label="päivän putki 5/5 ateriaa" />
    <StatTile tone="move" big value={`${data.exerciseLast7}/7`} label={`liikuntapäivää viime 7 pv, putki ${data.streaks.exerciseDays}`} />
  </div>

  <h2 class="section-title">Paino</h2>
  <div class="card">
    <div class="row w-head">
      <div>
        <b class="num w-now">{weightChart && weightChart.pts.length ? fmtKg(weightChart.pts[weightChart.pts.length - 1].kg) : '-'}</b>
        <span class="w-unit">kg nyt</span>
      </div>
      {#if weightChart?.enough}
        <Pill tone={weightChart.delta < 0 ? 'good' : 'warn'}>{fmtDelta(weightChart.delta)} kg / 6 vk</Pill>
      {/if}
    </div>
    {#if weightChart?.enough}
      <svg class="chart" viewBox="0 0 {W} {H}" role="img" aria-label="Painon kehitys viimeiset 6 viikkoa">
        {#each weightChart.gridLines as v (v)}
          <line class="grid-l" x1={L} x2={W - R} y1={weightChart.ys(v)} y2={weightChart.ys(v)} />
          <text x={L - 6} y={weightChart.ys(v) + 3} text-anchor="end">{v}</text>
        {/each}
        <path class="area" d={weightChart.area} />
        <path class="line" d={weightChart.path} />
        {#each weightChart.pts as p, k (p.date)}
          <circle class="pt" cx={xs(p.i)} cy={weightChart.ys(p.kg)} r={k === weightChart.pts.length - 1 ? 5 : 3}>
            <title>{fmtDay(p.date)}: {fmtKg(p.kg)} kg</title>
          </circle>
        {/each}
        <text class="val" x={xs(weightChart.last.i) - 9} y={weightChart.ys(weightChart.last.kg) - 9} text-anchor="end">{fmtKg(weightChart.last.kg)} kg</text>
        {#each weightChart.xLabels as l (l.text)}
          <text x={l.x} y={H - 8} text-anchor="middle">{l.text}</text>
        {/each}
      </svg>
    {:else}
      <div class="tip">Kirjaa paino muutamana aamuna, niin käyrä piirtyy tähän.</div>
    {/if}
  </div>

  <h2 class="section-title">Viikot</h2>
  <div class="card">
    <svg class="chart" viewBox="0 0 {W} {WH}" role="img" aria-label="Viikoittaiset päivät 5/5 ja liikuntapäivät">
      {#each [0, 7] as v (v)}
        <line class="grid-l" x1={L} x2={W - R} y1={yb(v)} y2={yb(v)} />
        <text x={L - 6} y={yb(v) + 3} text-anchor="end">{v}</text>
      {/each}
      {#each data.weeks as wk, k (wk.weekStart)}
        {@const x0 = L + gw * k + gw / 2 - bw - gap / 2}
        {@const x1 = x0 + bw + gap}
        {@const elapsed = Math.min(7, Math.max(0, wk.daysElapsed))}
        <rect class="bar ghost" x={x0} y={yb(elapsed)} width={bw} height={yb(0) - yb(elapsed)} />
        <rect class="bar" x={x0} y={yb(wk.fullDays)} width={bw} height={Math.max(0, yb(0) - yb(wk.fullDays))}>
          <title>{wk.fullDays} päivää 5/5</title>
        </rect>
        <rect class="bar ghost" x={x1} y={yb(elapsed)} width={bw} height={yb(0) - yb(elapsed)} />
        <rect class="bar mv" x={x1} y={yb(wk.exerciseDays)} width={bw} height={Math.max(0, yb(0) - yb(wk.exerciseDays))}>
          <title>{wk.exerciseDays} liikuntapäivää</title>
        </rect>
        {#if wk.fullDays}
          <text class="val" x={x0 + bw / 2} y={yb(wk.fullDays) - 4} text-anchor="middle">{wk.fullDays}</text>
        {/if}
        {#if wk.exerciseDays}
          <text class="val" x={x1 + bw / 2} y={yb(wk.exerciseDays) - 4} text-anchor="middle">{wk.exerciseDays}</text>
        {/if}
        <text x={L + gw * k + gw / 2} y={WH - 10} text-anchor="middle">{k === data.weeks.length - 1 ? 'tämä vk' : `vk ${wk.isoWeek}`}</text>
      {/each}
    </svg>
    <div class="legend">
      <span><i class="lg-accent"></i>Päiviä 5/5 ateriaa</span>
      <span><i class="lg-move"></i>Liikuntapäiviä</span>
    </div>
  </div>

  <h2 class="section-title">Viimeiset 30 päivää</h2>
  <div class="stat-row">
    <StatTile tone="good" value={`${data.last30.fullDays}/30`} label="päivää 5/5" />
    <StatTile tone="move" value={`${data.last30.exerciseDays}/30`} label="liikuntapäivää" />
    <StatTile value={`${data.last30.loggedDays}/30`} label="päivää merkattu" />
  </div>
{/if}

<style>
  .topbar {
    position: sticky;
    top: 0;
    z-index: 5;
    background: linear-gradient(var(--viisi-ground) 80%, transparent);
    padding: 18px 0 12px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .topbar h1 { font-size: 26px; font-weight: 800; letter-spacing: -0.01em; flex: 1; }
  .topbar .sub { color: var(--viisi-ink-2); font-size: 13px; }
  .grow { flex: 1; min-width: 0; }
  .row { display: flex; align-items: center; gap: 10px; }
  .card {
    background: var(--viisi-surface);
    border-radius: var(--viisi-radius);
    padding: 14px;
    box-shadow: var(--viisi-shadow);
  }
  .section-title { margin: 22px 0 10px; font-size: 17px; font-weight: 700; }
  .tip { font-size: 12.5px; color: var(--viisi-ink-3); margin-top: 8px; }

  .streak { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .stat-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }

  .w-head { justify-content: space-between; margin-bottom: 6px; }
  .w-now { font-family: var(--viisi-font-display); font-size: 24px; font-weight: 800; }
  .w-unit { color: var(--viisi-ink-2); font-size: 13px; }

  .chart { width: 100%; height: auto; display: block; }
  .chart text { font-family: var(--viisi-font-body); font-size: 10px; fill: var(--viisi-ink-2); }
  .chart .grid-l { stroke: var(--viisi-line); stroke-width: 1; }
  .chart .line {
    fill: none;
    stroke: var(--viisi-accent);
    stroke-width: 2;
    stroke-linejoin: round;
    stroke-linecap: round;
  }
  .chart .area { fill: var(--viisi-accent); opacity: 0.12; }
  .chart .pt { fill: var(--viisi-accent); stroke: var(--viisi-surface); stroke-width: 2; }
  .chart .bar { fill: var(--viisi-accent); rx: 3; }
  .chart .bar.mv { fill: var(--viisi-move); }
  .chart .bar.ghost { fill: var(--viisi-surface-2); }
  .chart .val { fill: var(--viisi-ink); font-weight: 600; }

  .legend { display: flex; gap: 14px; font-size: 12px; color: var(--viisi-ink-2); margin-top: 8px; flex-wrap: wrap; }
  .legend i { display: inline-block; width: 12px; height: 8px; border-radius: 3px; margin-right: 5px; }
  .lg-accent { background: var(--viisi-accent); }
  .lg-move { background: var(--viisi-move); }
</style>
